import { useState } from "react";
import { Button, List, Modal, Nav, Panel, Placeholder, SelectPicker, Stack, Tag } from "rsuite";
import styles from "../styles/importPage.module.scss";
import { ImportMethodsUtils } from "../../../core/utils/ImportMethodsUtils";
import { formatToBRLCurrency } from "../../../utilities/formatToBRLCurrency";

interface ResultModalProps {
  open: boolean;
  result: any | null;
  onClose: () => void;
  onConfirm: () => void;
  isConfirming?: boolean;
  integrationsSelect: { value: number, label: string }[];
  selectedIntegration: number;
  onSelectIntegration: (value: number) => void;
}

export default function ResultModal({
  open,
  result,
  onClose,
  onConfirm,
  integrationsSelect,
  selectedIntegration,
  onSelectIntegration,
  isConfirming = false,
}: ResultModalProps) {
  const [entityTab, setEntityTab] = useState<"clients" | "accounts">("accounts");
  const [statusTab, setStatusTab] = useState<"ok" | "attention" | "err">("ok")

  return (
    <Modal open={open} onClose={onClose} size="lg" backdrop="static">
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title>Pré visualização da importação</Modal.Title>
        <SelectPicker
          data={integrationsSelect}
          value={selectedIntegration}
          onChange={(value) => onSelectIntegration(value as number)}
          disabled
        />
      </Modal.Header>

      <Modal.Body>
        {result ? (
          <>
            <Nav
              appearance="tabs"
              activeKey={entityTab}
              onSelect={(key) => setEntityTab(key as "clients" | "accounts")}
              className="mb-2"
            >
              <Nav.Item eventKey="clients">
                {`Clientes / Fornecedores (${result.summary.clientsOk} ok / ${result.summary.clientsAttention} atenção / ${result.summary.clientsErr} erros)`}
              </Nav.Item>
              <Nav.Item eventKey="accounts">
                {`Contas (${result.summary.accountsOk} ok / ${result.summary.accountsAttention} atenção / ${result.summary.accountsErr} erros)`}
              </Nav.Item>
            </Nav>

            <Nav
              appearance="subtle"
              activeKey={statusTab}
              onSelect={(key) => setStatusTab(key as "ok" | "attention" | "err")}
              className="mb-3"
            >
              <Nav.Item eventKey="ok">
                {entityTab === "clients"
                  ? `Sucesso (${result.clients.ok.length})`
                  : `Sucesso (${result.accounts.ok.length})`}
              </Nav.Item>
              <Nav.Item eventKey="attention">
                {entityTab === "clients"
                  ? `Atenção (${result.clients.attentions?.length ?? 0})`
                  : `Atenção (${result.accounts.attentions?.length ?? 0})`}
              </Nav.Item>
              <Nav.Item eventKey="err">
                {entityTab === "clients"
                  ? `Erros (${result.clients.errors.length})`
                  : `Erros (${result.accounts.errors.length})`}
              </Nav.Item>
            </Nav>

            <Panel bordered bodyFill className={styles.resultPanel}>
              {entityTab === "clients" && statusTab === "ok" && (
                <>
                  {result.clients.ok.length === 0 ? (
                    <Placeholder.Paragraph rows={1} />
                  ) : (
                    <List bordered>
                      {result.clients.ok.map((c: any) => (
                        <List.Item key={`cok-${c.rowInSheet}`} className={styles.resultItemOk}>
                          <Stack justifyContent="space-between" alignItems="center">
                            <span>
                              <strong>Linha {c.rowInSheet}</strong> — {c.name} ({c.cpfcnpj})
                            </span>
                            <Tag color="green">OK</Tag>
                          </Stack>
                          <div className={styles.resultMsg}>
                            {c.type}
                            {c.email ? ` • ${c.email}` : ""}
                            {c.fantasyName ? ` • ${c.fantasyName}` : ""}
                          </div>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </>
              )}

              {entityTab === "clients" && statusTab === "attention" && (
                <>
                  {(result.clients.attentions?.length ?? 0) === 0 ? (
                    <Placeholder.Paragraph rows={1} />
                  ) : (
                    <List bordered>
                      {result.clients.attentions.map((e: any) => (
                        <List.Item key={`catt-${e.rowInSheet}`} className={styles.resultItemAttention}>
                          <Stack justifyContent="space-between" alignItems="center">
                            <span>
                              <strong>Linha {e.rowInSheet}</strong> — {e.item.name || "(sem nome)"}
                              {e.item.cpfcnpj ? ` (${e.item.cpfcnpj})` : ""}
                            </span>
                            <Tag color="orange">Atenção</Tag>
                          </Stack>
                          <div className={styles.resultMsg}>
                            {ImportMethodsUtils.summarizeErrors(e.attentions)}
                          </div>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </>
              )}

              {entityTab === "clients" && statusTab === "err" && (
                <>
                  {result.clients.errors.length === 0 ? (
                    <Placeholder.Paragraph rows={1} />
                  ) : (
                    <List bordered>
                      {result.clients.errors.map((e: any) => (
                        <List.Item key={`cerr-${e.rowInSheet}`} className={styles.resultItemErr}>
                          <Stack justifyContent="space-between" alignItems="center">
                            <span>
                              <strong>Linha {e.rowInSheet}</strong> — {e.item.name || "(sem nome)"}
                              {e.item.cpfcnpj ? ` (${e.item.cpfcnpj})` : ""}
                            </span>
                            <Tag color="red">Erro</Tag>
                          </Stack>
                          <div className={styles.resultMsg}>
                            {ImportMethodsUtils.summarizeErrors(e.errors)}
                          </div>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </>
              )}

              {entityTab === "accounts" && statusTab === "ok" && (
                <>
                  {result.accounts.ok.length === 0 ? (
                    <Placeholder.Paragraph rows={1} />
                  ) : (
                    <List bordered>
                      {result.accounts.ok.map((a: any) => (
                        <List.Item key={`aok-${a.rowInSheet}`} className={styles.resultItemOk}>
                          <Stack justifyContent="space-between" alignItems="center">
                            <span>
                              <strong>Linha {a.rowInSheet}</strong> — {a.cpfcnpj} • {a.type}
                            </span>
                            <Tag color="green">OK</Tag>
                          </Stack>
                          <div className={styles.resultMsg}>
                            {/* {`Venc. ${a.dueDate}`} */}
                            {` • Valor R$ ${formatToBRLCurrency(String(a.rawValue)) || "-"}`}
                            {a.documentNumber ? ` • Doc ${a.documentNumber}` : ""}
                          </div>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </>
              )}

              {entityTab === "accounts" && statusTab === "attention" && (
                <>
                  {(result.accounts.attentions?.length ?? 0) === 0 ? (
                    <Placeholder.Paragraph rows={1} />
                  ) : (
                    <List bordered>
                      {result.accounts.attentions.map((e: any) => (
                        <List.Item key={`aatt-${e.rowInSheet}`} className={styles.resultItemAttention}>
                          <Stack justifyContent="space-between" alignItems="center">
                            <span>
                              <strong>Linha {e.rowInSheet}</strong> — {e.item.cpfcnpj || "(sem cpf/cnpj)"}
                            </span>
                            <Tag color="orange">Atenção</Tag>
                          </Stack>
                          <div className={styles.resultMsg}>
                            {ImportMethodsUtils.summarizeErrors(e.attentions)}
                          </div>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </>
              )}

              {entityTab === "accounts" && statusTab === "err" && (
                <>
                  {result.accounts.errors.length === 0 ? (
                    <Placeholder.Paragraph rows={1} />
                  ) : (
                    <List bordered>
                      {result.accounts.errors.map((e: any) => (
                        <List.Item key={`aerr-${e.rowInSheet}`} className={styles.resultItemErr}>
                          <Stack justifyContent="space-between" alignItems="center">
                            <span>
                              <strong>Linha {e.rowInSheet}</strong> — {e.item.cpfcnpj || "(sem cpf/cnpj)"}
                              {e.item.type ? ` • ${e.item.type}` : ""}
                            </span>
                            <Tag color="red">Erro</Tag>
                          </Stack>
                          <div className={styles.resultMsg}>
                            {ImportMethodsUtils.summarizeErrors(e.errors)}
                          </div>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </>
              )}
            </Panel>
          </>
        ) : (
          <Placeholder.Paragraph rows={4} />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Stack spacing={8} className={styles.resultModalFooter}>
          <Button appearance="primary" onClick={onConfirm} disabled={!result} loading={isConfirming}>
            Importar registros válidos
          </Button>
          <Button appearance="subtle" onClick={onClose}>
            Fechar
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}
